import { useCallback } from 'react';

import { Product } from 'types/graphql-overrides';
import { tracker } from 'src/utils/analytics';
import { getLogger } from 'src/utils/datadog-logger';
import { useFetchTracker } from '../use-fetch-tracker';
import { AdPlacement } from '../../placements';

const logger = getLogger('ads.sponsored-products');

type SponsoredProductHandlersInput = {
  trackerSource: string;
  adPlacement: AdPlacement;
};

type SponsoredProductHandlersReturn = {
  handleSponsoredProductClick: (product: Product, productIndex: number) => void;
  handleSponsoredProductImpression: (product: Product, productIndex: number) => void;
};

export function useSponsoredProductHandlers({
  trackerSource,
  adPlacement,
}: SponsoredProductHandlersInput): SponsoredProductHandlersReturn {
  const fetchTracker = useFetchTracker();

  const handleSponsoredProductImpression = useCallback(
    (product: Product, productIndex: number) => {
      const { impressionUrl } = product.adTrackers ?? {};

      if (impressionUrl) {
        logger.info('handleSponsoredProductImpression', { impressionUrl, trackerSource });

        void fetchTracker(impressionUrl, {
          uiPlacement: adPlacement,
          uiPlacementIndex: productIndex,
          trackingParams: { position: String(productIndex + 1) },
        });

        tracker.sponsoredProductImpression({ product, trackerSource, position: productIndex });
      }
    },
    [adPlacement, fetchTracker, trackerSource]
  );

  const handleSponsoredProductClick = useCallback(
    (product: Product, productIndex: number) => {
      const { clickUrl } = product.adTrackers ?? {};

      if (clickUrl) {
        logger.info('handleSponsoredProductClick', { clickUrl, trackerSource });

        void fetchTracker(clickUrl, {
          uiPlacement: adPlacement,
          uiPlacementIndex: productIndex,
          trackingParams: { position: String(productIndex + 1) },
        });

        tracker.sponsoredProductClicked({ product, trackerSource, position: productIndex });
        tracker.setContext({ activePromotion: { product, trackerSource } });
      }
    },
    [adPlacement, fetchTracker, trackerSource]
  );

  return {
    handleSponsoredProductImpression,
    handleSponsoredProductClick,
  };
}
